import { useC6Bank } from "@/app/contexts/C6BankContext";
import { c6BankService } from "@/app/services/c6BankService";
import {
  PostSimulationParams,
  SimulationCondition,
} from "@/app/services/c6BankService/postSimulation";
import masks from "@/app/utils/masks";
import debounce from "lodash.debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext";
import { PartnerType } from "@/app/utils/partners.enum";

type OffersLocationState = {
  valorParcela?: number;
  prazo?: number;
};

export default function useC6BankOffers() {
  const { cpf } = useParams();
  const location = useLocation();

  const { valorParcela, prazo } =
    (location.state as OffersLocationState | null) ?? {};

  const [loading, setLoading] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<SimulationCondition[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [commission, setCommission] = useState(0);
  const [netAmount, setNetAmount] = useState(0);
  const [installmentValue, setInstallmentValue] = useState(0);
  const [installmentInput, setInstallmentInput] = useState("");
  const [installmentOptions, setInstallmentOptions] = useState<number[]>([]);
  const [selectedInstallment, setSelectedInstallment] = useState(0);

  const currentInstallmentRef = useRef(0);

  const { customerData, setCustomerData, clearCustomerData, setProposalData } =
    useC6Bank();

  const fetchSimulation = async (
    params: PostSimulationParams,
    isUpdate = false,
  ) => {
    try {
      if (isUpdate) {
        setLoadingOffers(true);
        setError(null);
      } else {
        setLoading(true);
        setError(null);
      }

      const response = await c6BankService.postSimulation(params);

      setOffers(response.condicoes_credito);
      setCommission(response.commission);

      const firstOffer = response.condicoes_credito[0];

      setNetAmount(firstOffer?.condicao?.valor_liquido ?? 0);
      setInstallmentValue(firstOffer?.condicao?.valor_parcela ?? 0);

      currentInstallmentRef.current = firstOffer?.condicao?.valor_parcela ?? 0;

      if (!isUpdate) {
        setInstallmentOptions(response.installments);
        setSelectedInstallment(
          prazo ??
            response.installments[response.installments.length - 1] ??
            0,
        );
      }

      setCurrentIndex(0);
    } catch (error: any) {
      const message = error?.message || "Erro ao buscar simulações";
      setError(message);
    } finally {
      if (isUpdate) {
        setLoadingOffers(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!cpf) return;

    fetchSimulation({
      cpf,
      typeSimulation: "POR_VALOR_PARCELA",
      ...(valorParcela ? { valor_parcela: valorParcela } : {}),
      ...(prazo ? { Prazo: prazo } : {}),
    });
  }, [cpf]);

  const debouncedFetch = useRef(
    debounce((valorParcela: number, prazo: number) => {
      if (!cpf) return;

      fetchSimulation(
        {
          cpf,
          typeSimulation: "POR_VALOR_PARCELA",
          valor_parcela: valorParcela,
          Prazo: prazo,
        },
        true,
      );
    }, 1200),
  ).current;

  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  useEffect(() => {
    setInstallmentInput(
      masks.currency(String(Math.round(installmentValue * 100))),
    );
  }, [installmentValue]);

  const netAmountValue = useMemo(
    () => masks.currency(String(Math.round(netAmount * 100))),
    [netAmount],
  );

  const handleSelectInstallment = (prazo: number) => {
    setSelectedInstallment(prazo);

    if (currentInstallmentRef.current) {
      fetchSimulation(
        {
          cpf: cpf!,
          typeSimulation: "POR_VALOR_PARCELA",
          valor_parcela: currentInstallmentRef.current,
          Prazo: prazo,
        },
        true,
      );
    }
  };

  const handleConfirm = () => {
    const selected = offers[currentIndex];

    if (!selected) return;

    const condicao = selected.condicao;

    if (customerData?.cpf && customerData.cpf !== cpf) clearCustomerData();

    setCustomerData({
      cpf,
    });

    setProposalData({
      idSimulation: condicao.id_simulacao,
      value: condicao.valor_liquido,
      prazo: condicao.quantidade_parcelas,
    });
  };

  useRegisterSupportContext("simulation.c6.offers", {
    product: PartnerType.C6,
    step: "Ofertas C6",
    data: {
      cpf,
      installments: selectedInstallment || undefined,
      installmentValue: installmentValue || undefined,
      netAmount: netAmount || undefined,
      selectedOffer: offers[currentIndex],
    },
  });

  return {
    loading,
    loadingOffers,
    error,
    offers,
    currentIndex,
    setCurrentIndex,
    commission,
    debouncedFetch,
    handleConfirm,
    netAmountValue,
    installmentInput,
    setInstallmentInput,
    installmentOptions,
    selectedInstallment,
    handleSelectInstallment,
  };
}
